import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Loader2,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import EngagementPersonaTable from '@/components/analytics/EngagementPersonaTable';
import EngagementViewFormDialog from '@/components/analytics/EngagementViewFormDialog';
import {
  applyEngagementViewConfig,
  buildViewConfigSummaryChips,
  collectProductAffinityOptions,
  createEngagementSavedView,
  deleteEngagementSavedView,
  fetchEngagementSavedViews,
  updateEngagementSavedView,
} from '@/lib/engagementSavedViews';
import {
  getPersonaArchetypeName,
  getPersonaTableDepartment,
  sortEngagementPersonas,
} from '@/lib/engagementPersonaUtils';

export default function EngagementShortlistPanel({
  combinedPersonas,
  personaPoolLoading,
  tenantId,
  userId,
  getAccountFitScoreForRow,
  getRowAccountTier,
  getRowAccountSector,
  accountTypeOptions,
  accountIdByNameMap,
}) {
  const [views, setViews] = useState([]);
  const [viewsLoading, setViewsLoading] = useState(true);
  const [reorderMode, setReorderMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editingView, setEditingView] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineEditName, setInlineEditName] = useState('');
  const [openCards, setOpenCards] = useState({});
  const [sortByViewId, setSortByViewId] = useState({});

  const loadViews = useCallback(async () => {
    if (!tenantId) return;
    setViewsLoading(true);
    try {
      const list = await fetchEngagementSavedViews(tenantId, userId);
      setViews(list);
      setOpenCards((prev) => {
        const next = { ...prev };
        list.forEach((v) => {
          if (next[v.id] === undefined) next[v.id] = true;
        });
        return next;
      });
    } catch {
      setViews([]);
    } finally {
      setViewsLoading(false);
    }
  }, [tenantId, userId]);

  useEffect(() => {
    loadViews();
  }, [loadViews]);

  const archetypeOptions = useMemo(() => {
    const set = new Set();
    combinedPersonas.forEach((p) => {
      const name = getPersonaArchetypeName(p);
      if (name) set.add(name);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [combinedPersonas]);

  const departmentOptions = useMemo(() => {
    const set = new Set();
    combinedPersonas.forEach((p) => {
      const d = getPersonaTableDepartment(p);
      if (d) set.add(d);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [combinedPersonas]);

  const productAffinityOptions = useMemo(
    () => collectProductAffinityOptions(combinedPersonas),
    [combinedPersonas],
  );

  const filterHelpers = useMemo(
    () => ({ getRowAccountTier, getRowAccountSector }),
    [getRowAccountTier, getRowAccountSector],
  );

  const getSortedRowsForView = useCallback(
    (view) => {
      const cfg = view?.view_config || {};
      const filtered = applyEngagementViewConfig(combinedPersonas, cfg, filterHelpers);
      const sortConfig = sortByViewId[view.id] || { key: 'persona_fit_score', direction: 'desc' };
      return sortEngagementPersonas(filtered, sortConfig, {
        getAccountFitScoreForRow,
      });
    },
    [combinedPersonas, filterHelpers, sortByViewId, getAccountFitScoreForRow],
  );

  const openCreateDialog = () => {
    setDialogMode('create');
    setEditingView(null);
    setNameError('');
    setDialogOpen(true);
  };

  const openEditDialog = (view) => {
    setDialogMode('edit');
    setEditingView(view);
    setNameError('');
    setDialogOpen(true);
  };

  const handleSaveView = async ({ name, view_config }) => {
    setSaving(true);
    setNameError('');
    try {
      if (dialogMode === 'edit' && editingView?.id) {
        const updated = await updateEngagementSavedView(tenantId, userId, editingView.id, {
          name,
          view_config,
        });
        setViews((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
      } else {
        const sort_order = views.length;
        const created = await createEngagementSavedView(tenantId, userId, {
          name,
          view_config,
          sort_order,
        });
        setViews((prev) => [...prev, created].sort(
          (a, b) => (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0),
        ));
      }
      setDialogOpen(false);
    } catch (err) {
      if (err?.status === 409) {
        setNameError(err.message || 'A view with this name already exists');
      } else {
        setNameError(err?.message || 'Failed to save view');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteView = async (view) => {
    if (!window.confirm(`Delete "${view.name}"? This cannot be undone.`)) return;
    try {
      await deleteEngagementSavedView(tenantId, userId, view.id);
      setViews((prev) => prev.filter((v) => v.id !== view.id));
    } catch {
      window.alert('Failed to delete view. Please try again.');
    }
  };

  const commitInlineRename = async (view) => {
    const trimmed = inlineEditName.trim();
    setInlineEditId(null);
    if (!trimmed || trimmed === view.name) return;
    try {
      const updated = await updateEngagementSavedView(tenantId, userId, view.id, { name: trimmed });
      setViews((prev) => prev.map((v) => (v.id === updated.id ? updated : v)));
    } catch (err) {
      if (err?.status === 409) {
        window.alert(err.message || 'A view with this name already exists');
      }
    }
  };

  const moveView = async (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= views.length) return;
    const reordered = [...views];
    const [item] = reordered.splice(index, 1);
    reordered.splice(target, 0, item);
    setViews(reordered);
    try {
      await Promise.all(
        reordered.map((v, i) =>
          updateEngagementSavedView(tenantId, userId, v.id, { sort_order: i }),
        ),
      );
      setViews(reordered.map((v, i) => ({ ...v, sort_order: i })));
    } catch {
      loadViews();
    }
  };

  const toggleSort = (viewId, key) => {
    setSortByViewId((prev) => {
      const cur = prev[viewId] || { key: 'persona_fit_score', direction: 'desc' };
      return {
        ...prev,
        [viewId]: {
          key,
          direction: cur.key === key && cur.direction === 'desc' ? 'asc' : 'desc',
        },
      };
    });
  };

  if (personaPoolLoading || viewsLoading) {
    return (
      <Card>
        <CardContent className="p-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#00C2C2]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <p className="text-sm text-gray-600">
          <strong>{views.length}</strong> saved view{views.length === 1 ? '' : 's'} · filtered from{' '}
          <strong>{combinedPersonas.length}</strong> personas
        </p>
        <div className="flex items-center gap-2">
          {views.length > 1 ? (
            <Button
              type="button"
              variant={reorderMode ? 'secondary' : 'outline'}
              size="sm"
              className="text-xs"
              onClick={() => setReorderMode((v) => !v)}
            >
              <GripVertical className="w-3.5 h-3.5 mr-1" />
              {reorderMode ? 'Done reordering' : 'Reorder'}
            </Button>
          ) : null}
          <Button type="button" size="sm" className="text-xs bg-[#00C2C2] hover:bg-[#00A3A3]" onClick={openCreateDialog}>
            <Plus className="w-3.5 h-3.5 mr-1" />
            New view
          </Button>
        </div>
      </div>

      {views.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-gray-500">
            No saved views yet. Create one to build your shortlist.
          </CardContent>
        </Card>
      ) : (
        views.map((view, index) => {
          const chips = buildViewConfigSummaryChips(view.view_config);
          const rows = getSortedRowsForView(view);
          const isOpen = openCards[view.id] !== false;
          const sortConfig = sortByViewId[view.id] || { key: 'persona_fit_score', direction: 'desc' };

          return (
            <Collapsible
              key={view.id}
              open={isOpen}
              onOpenChange={(open) => setOpenCards((prev) => ({ ...prev, [view.id]: open }))}
            >
              <Card>
                <CardHeader className="py-3 px-4 space-y-2">
                  <div className="flex items-start gap-2">
                    {reorderMode ? (
                      <div className="flex flex-col gap-0.5 shrink-0 pt-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          disabled={index === 0}
                          onClick={() => moveView(index, -1)}
                          aria-label="Move up"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </Button>
                        <GripVertical className="w-4 h-4 text-gray-400 mx-auto" aria-hidden />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          disabled={index === views.length - 1}
                          onClick={() => moveView(index, 1)}
                          aria-label="Move down"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : null}

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <CollapsibleTrigger asChild>
                          <button
                            type="button"
                            className="text-left font-semibold text-[#0A1A2F] hover:text-[#00C2C2] text-sm"
                          >
                            {inlineEditId === view.id ? (
                              <Input
                                className="h-8 max-w-xs"
                                value={inlineEditName}
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => setInlineEditName(e.target.value)}
                                onBlur={() => commitInlineRename(view)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') commitInlineRename(view);
                                  if (e.key === 'Escape') setInlineEditId(null);
                                }}
                              />
                            ) : (
                              <span
                                onDoubleClick={(e) => {
                                  e.stopPropagation();
                                  setInlineEditId(view.id);
                                  setInlineEditName(view.name);
                                }}
                              >
                                {view.name}
                              </span>
                            )}
                          </button>
                        </CollapsibleTrigger>
                        <Badge variant="secondary" className="text-xs font-normal">
                          {rows.length} match{rows.length === 1 ? '' : 'es'}
                        </Badge>
                      </div>
                      {chips.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {chips.map((chip) => (
                            <Badge key={chip} variant="outline" className="text-[10px] font-normal">
                              {chip}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">No filters — all personas</p>
                      )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label="Edit view"
                        onClick={() => openEditDialog(view)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-red-600 hover:text-red-700"
                        aria-label="Delete view"
                        onClick={() => handleDeleteView(view)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CollapsibleContent>
                  <div className="px-4 pb-4">
                    <EngagementPersonaTable
                      rows={rows}
                      sortConfig={sortConfig}
                      onToggleSort={(key) => toggleSort(view.id, key)}
                      emptyMessage="No personas match — tweak filters."
                      getAccountFitScoreForRow={getAccountFitScoreForRow}
                      accountIdByNameMap={accountIdByNameMap}
                    />
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          );
        })
      )}

      <EngagementViewFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        initialName={editingView?.name || ''}
        initialViewConfig={editingView?.view_config}
        archetypeOptions={archetypeOptions}
        departmentOptions={departmentOptions}
        accountTypeOptions={accountTypeOptions}
        productAffinityOptions={productAffinityOptions}
        onSave={handleSaveView}
        saving={saving}
        nameError={nameError}
      />
    </div>
  );
}
